import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Shuffle,
    ChevronsLeft,
    Clock,
    AlertCircle,
    CheckCircle,
    Calendar,
    Users,
    History,
    Gift,
    Eye,
    Key,
    Ticket,
    DollarSign,
    MapPin
} from 'lucide-react';
import marketingService from '../services/marketingService';
import LightMixTree from './LightMixTree';

const LightMixCluster = ({ onBack }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Основные состояния
    const [loading, setLoading] = useState(true);
    const [currentDraw, setCurrentDraw] = useState(null);
    const [drawHistory, setDrawHistory] = useState([]);
    const [userPositions, setUserPositions] = useState([]); // Места пользователя в дереве
    const [pendingTickets, setPendingTickets] = useState(0); // Билеты в очереди
    const [isActivated, setIsActivated] = useState(false);
    const [activationDetails, setActivationDetails] = useState(null);

    // UI состояния
    const [activeTab, setActiveTab] = useState('current');
    const [showTree, setShowTree] = useState(false);
    const [treeParams, setTreeParams] = useState(null);

    // Покупка
    const [isPurchasing, setIsPurchasing] = useState(false);

    // Таймер до розыгрыша
    const [drawTimeRemaining, setDrawTimeRemaining] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (currentDraw) {
            checkUserStatus();
        }
    }, [currentDraw]);

    // Таймер до розыгрыша
    useEffect(() => {
        if (!currentDraw?.draw_date) {
            setDrawTimeRemaining(null);
            return;
        }

        const targetTime = new Date(currentDraw.draw_date).getTime();

        const updateDrawTimer = () => {
            const now = new Date().getTime();
            const distance = targetTime - now;

            if (distance <= 0) {
                setDrawTimeRemaining(null);
                return true;
            }

            setDrawTimeRemaining(distance);
            return false;
        };

        const shouldStop = updateDrawTimer();
        if (shouldStop) return;

        const interval = setInterval(() => {
            const shouldStop = updateDrawTimer();
            if (shouldStop) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [currentDraw]);

    const formatDrawTime = (milliseconds) => {
        if (!milliseconds || milliseconds <= 0) return null;

        const totalSeconds = Math.floor(milliseconds / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (days > 0) {
            return { days, hours, minutes, seconds, display: `${days}д ${hours}ч ${minutes}м` };
        }
        return {
            days: 0,
            hours,
            minutes,
            seconds,
            display: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        };
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [drawResponse, historyResponse] = await Promise.all([
                marketingService.getLightMixCurrentDraw(),
                marketingService.getLightMixHistory()
            ]);

            // Устанавливаем draw (может быть null - это нормально)
            setCurrentDraw(drawResponse?.success ? drawResponse.data?.draw : null);

            // Устанавливаем историю
            setDrawHistory(historyResponse?.success ? (historyResponse.data?.history || []) : []);

        } catch (error) {
            console.error('Error loading data:', error);
            setCurrentDraw(null);
            setDrawHistory([]);
        } finally {
            setLoading(false);
        }
    };

    const checkUserStatus = async () => {
        if (!currentDraw) return;

        try {
            const response = await marketingService.getLightMixUserStatus(currentDraw.id);

            if (response.success) {
                setUserPositions(response.data.positions || []);
                setPendingTickets(response.data.pending_count || 0);
                setIsActivated(response.data.is_activated || false);
                setActivationDetails(response.data.activation_details);
            }
        } catch (error) {
            console.error('Error checking user status:', error);
        }
    };

    const handlePurchase = async () => {
        if (!currentDraw || isPurchasing) return;

        setIsPurchasing(true);

        try {
            const response = await marketingService.purchaseLightMixPlace({
                draw_id: currentDraw.id,
                program: currentDraw.program
            });

            if (response.success) {
                setPendingTickets(prev => prev + 1);
                checkUserStatus();
                loadData(); // Обновляем общую статистику
            } else {
                alert(response.message || 'Ошибка при покупке билета');
            }
        } catch (error) {
            alert(error.message || 'Ошибка при покупке билета');
        } finally {
            setIsPurchasing(false);
        }
    };

    const handleViewTree = async (drawId) => {
        try {
            const response = await marketingService.getLightMixUserStatus(drawId);

            if (response.success) {
                if (response.data.is_activated || response.data.positions?.length > 0) {
                    setShowTree(true);
                    setTreeParams({ drawId: drawId });
                } else {
                    alert('Вы не участвуете в этом розыгрыше');
                }
            }
        } catch (error) {
            alert(error.message || 'Ошибка при проверке статуса');
        }
    };

    const handleBackFromTree = () => {
        setSearchParams({});
        setShowTree(false);
        setTreeParams(null);
    };

    const handleBack = () => {
        navigate('/dashboard/clusters');
    };

    // Рендер наград
    const renderRewards = (info) => {
        if (!info) return null;

        const rewards = [];

        if (info.awards && parseFloat(info.awards) > 0) {
            rewards.push(
                <div key="money" className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                    <DollarSign size={18} className="text-green-600" />
                    <span className="text-green-700 font-medium">{info.awards} USDT</span>
                </div>
            );
        }

        if (info.keys && parseInt(info.keys) > 0) {
            rewards.push(
                <div key="keys" className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                    <Key size={18} className="text-blue-600" />
                    <span className="text-blue-700 font-medium">Light Keys (ур. {info.keys})</span>
                </div>
            );
        }

        if (info.mix && parseInt(info.mix) > 0) {
            rewards.push(
                <div key="mix" className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg">
                    <Ticket size={18} className="text-purple-600" />
                    <span className="text-purple-700 font-medium">{info.mix} билет на след. Mix</span>
                </div>
            );
        }

        return rewards.length > 0 ? rewards : null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    // Показываем дерево
    if (showTree && treeParams) {
        return (
            <LightMixTree
                drawId={treeParams.drawId}
                onBack={handleBackFromTree}
            />
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Шапка */}
            <div className="border-b px-4 py-3">
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <button
                            onClick={onBack || (() => navigate('/clusters'))}
                            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <ChevronsLeft size={20} className="text-gray-500"/>
                        </button>
                        <div className="flex items-center">
                            <div
                                className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white mr-2">
                                <Shuffle size={16}/>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Light Mix</h3>
                        </div>
                    </div>

                    {/* Табы */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setActiveTab('current')}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                activeTab === 'current'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            Текущий
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                                activeTab === 'history'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <History size={16} />
                            История
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-4 md:p-6">
                {activeTab === 'current' ? (
                    <>
                        {/* Текущий розыгрыш */}
                        {currentDraw ? (
                            <div className="space-y-6">
                                {/* Карточка розыгрыша */}
                                <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-white rounded-2xl border-2 border-purple-200 overflow-hidden">
                                    {/* Заголовок */}
                                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                                                    {currentDraw.display_name || currentDraw.title}
                                                </h2>
                                                {currentDraw.description && (
                                                    <p className="text-purple-100 text-xs sm:text-sm mt-1 truncate">{currentDraw.description}</p>
                                                )}
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-2xl sm:text-3xl font-bold text-white">4 $</div>
                                                <div className="text-purple-100 text-xs sm:text-sm">за билет</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Таймер до розыгрыша */}
                                    {drawTimeRemaining && (
                                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-purple-100/50 to-pink-100/50 border-b border-purple-200">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={18} className="text-purple-600" />
                                                    <span className="text-gray-700 font-medium text-sm sm:text-base">До розыгрыша:</span>
                                                </div>
                                                <div className="flex items-center justify-end">
                                                    {formatDrawTime(drawTimeRemaining)?.days > 0 ? (
                                                        <div className="flex gap-1 sm:gap-2">
                                                            <div className="bg-purple-500 text-white px-2 sm:px-3 py-1 rounded-lg font-bold text-sm sm:text-base">
                                                                {formatDrawTime(drawTimeRemaining).days}д
                                                            </div>
                                                            <div className="bg-purple-500 text-white px-2 sm:px-3 py-1 rounded-lg font-bold text-sm sm:text-base">
                                                                {formatDrawTime(drawTimeRemaining).hours}ч
                                                            </div>
                                                            <div className="bg-purple-500 text-white px-2 sm:px-3 py-1 rounded-lg font-bold text-sm sm:text-base">
                                                                {formatDrawTime(drawTimeRemaining).minutes}м
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="bg-purple-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-bold text-base sm:text-lg tabular-nums">
                                                            {formatDrawTime(drawTimeRemaining)?.display}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Статистика розыгрыша */}
                                    <div className="px-4 sm:px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                        {/* Участников */}
                                        <div className="bg-white rounded-xl p-3 sm:p-4 border border-purple-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                                    <Users size={20} className="text-purple-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                        {currentDraw.participants_count || 0}
                                                    </div>
                                                    <div className="text-xs sm:text-sm text-gray-500">Участников</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Билетов (в очереди) */}
                                        <div className="bg-white rounded-xl p-3 sm:p-4 border border-yellow-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                                    <Ticket size={20} className="text-yellow-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                        {currentDraw.tickets_count || 0}
                                                    </div>
                                                    <div className="text-xs sm:text-sm text-gray-500">Билетов</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Мест (в дереве) */}
                                        <div className="bg-white rounded-xl p-3 sm:p-4 border border-green-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                                                    <MapPin size={20} className="text-green-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xl sm:text-2xl font-bold text-gray-800">
                                                        {currentDraw.places_count || 0}
                                                    </div>
                                                    <div className="text-xs sm:text-sm text-gray-500">Мест</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Награды */}
                                    {currentDraw.information && (
                                        <div className="px-4 sm:px-6 py-4 border-t border-purple-100">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
                                                <Gift size={16} className="text-purple-500" />
                                                Вознаграждения
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {renderRewards(currentDraw.information)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Мои билеты и места */}
                                    {(userPositions.length > 0 || pendingTickets > 0) && (
                                        <div className="px-4 sm:px-6 py-4 border-t border-purple-100 bg-gradient-to-r from-green-50/50 to-purple-50/50">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
                                                <Ticket size={16} className="text-purple-500" />
                                                Мой статус
                                            </h4>
                                            <div className="flex flex-wrap gap-2 sm:gap-4">
                                                {/* Билеты в очереди */}
                                                {pendingTickets > 0 && (
                                                    <div className="flex items-center gap-2 bg-yellow-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                                                        <Clock size={16} className="text-yellow-600" />
                                                        <span className="font-medium text-yellow-700 text-sm sm:text-base">
                                                            Билетов: {pendingTickets}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Места в дереве */}
                                                {userPositions.length > 0 && (
                                                    <div className="flex items-center gap-2 bg-green-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                                                        <CheckCircle size={16} className="text-green-600" />
                                                        <span className="font-medium text-green-700 text-sm sm:text-base">
                                                            Мест: {userPositions.length}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Всего */}
                                                <div className="flex items-center gap-2 bg-purple-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                                                    <Ticket size={16} className="text-purple-600" />
                                                    <span className="font-medium text-purple-700 text-sm sm:text-base">
                                                        Всего: {userPositions.length + pendingTickets}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Кнопка просмотра дерева */}
                                            {userPositions.length > 0 && (
                                                <button
                                                    onClick={() => handleViewTree(currentDraw.id)}
                                                    className="mt-4 w-full py-2.5 sm:py-3 rounded-xl font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 transition-all flex items-center justify-center gap-2 border border-purple-200 text-sm sm:text-base"
                                                >
                                                    <Eye size={18} />
                                                    Посмотреть моё дерево
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Кнопка покупки */}
                                    {currentDraw.status === 'active' && (
                                        <div className="px-6 py-4 border-t border-purple-100">
                                            <button
                                                onClick={handlePurchase}
                                                disabled={isPurchasing}
                                                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                                                    isPurchasing
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                                                }`}
                                            >
                                                {isPurchasing ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                                        Обработка...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Ticket size={24} />
                                                        Купить билет за 4 $
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Shuffle size={40} className="text-gray-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">Нет активных розыгрышей</h3>
                                <p className="text-gray-500">Следите за обновлениями, скоро будет новый розыгрыш!</p>
                            </div>
                        )}
                    </>
                ) : (
                    /* История розыгрышей */
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">История розыгрышей</h3>

                        {drawHistory.length > 0 ? (
                            drawHistory.map((draw) => (
                                <div
                                    key={draw.id}
                                    className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0">
                                                {draw.program?.replace('mix_', '#')}
                                            </div>
                                            <div className="min-w-0">
                                                <h4 className="font-semibold text-gray-800 truncate">
                                                    {draw.display_name || draw.title}
                                                </h4>
                                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={14} />
                                                        {draw.formatted_draw_date || draw.completed_at}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Users size={14} />
                                                        {draw.participants_count || 0}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 sm:gap-3 ml-auto sm:ml-0 flex-shrink-0">
                                            {draw.user_participated && (
                                                <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                                                    Участвовали
                                                </span>
                                            )}
                                            {draw.user_participated && (
                                                <button
                                                    onClick={() => handleViewTree(draw.id)}
                                                    className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all flex-shrink-0"
                                                    title="Посмотреть дерево"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                    <History size={32} className="text-gray-400" />
                                </div>
                                <p className="text-gray-500">История розыгрышей пуста</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LightMixCluster;
